import { useLoaderData } from "@tanstack/react-router";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { useScrollDepth } from "@/hooks/useScrollDepth";
import {
  fetchCollection,
  fetchProduct,
  fetchProducts,
  type ShopifyProduct,
} from "@/lib/shopify";
import { HeroMushroom } from "@/components/home/HeroMushroom";
import { AmbientBackdrop } from "@/components/home/AmbientBackdrop";
import { CategoryUniverse } from "@/components/home/CategoryUniverse";
import { ColorShopStrip } from "@/components/home/ColorShopStrip";
import { MuranoMatchInvite } from "@/components/home/MuranoMatchInvite";
import { WhyFarveladeland } from "@/components/home/WhyFarveladeland";
import { BrandPhilosophy } from "@/components/home/BrandPhilosophy";
import { TrustpilotReviews } from "@/components/home/TrustpilotReviews";

import { organizationJsonLd, websiteJsonLd, localizedHead } from "@/lib/seo";
import heroMushroomImg from "@/assets/farve/coll-mushroom.jpg";

const HERO_HANDLE = "vintage-murano-peacock-loftlampe-42cm";
const HERO_IMAGE_INDEX = 4;

export const homeLoader = async () => {
  const [
    ceiling,
    mushroom,
    wall,
    latest,
    heroProduct,
    catNyheder,
    catBordet,
    catBelysning,
    catInterior,
    catMurano,
    catUnikke,
  ] = await Promise.all([
    fetchCollection("murano-loftlamper", 6),
    fetchCollection("murano-mushroom-lampe", 6),
    fetchCollection("murano-vaeglamper", 6),
    fetchProducts({ first: 12, sortKey: "CREATED_AT", reverse: true }),
    fetchProduct(HERO_HANDLE),
    fetchCollection("nyheder", 4),
    fetchCollection("borddaekning", 4),
    fetchCollection("vintage-lamper", 4),
    fetchCollection("interior", 4),
    fetchCollection("murano-lamper", 4),
    fetchCollection("sidste-chance-farvelader", 4),
  ]);

  const heroEdges = heroProduct?.images?.edges ?? [];
  const pickedHero =
    heroEdges[HERO_IMAGE_INDEX]?.node?.url ?? heroEdges[0]?.node?.url ?? null;
  const heroFallback = ceiling?.products?.edges?.[0] as ShopifyProduct | undefined;
  const heroNode = heroProduct ?? heroFallback?.node ?? null;
  const heroImage = pickedHero ?? heroFallback?.node?.images?.edges?.[0]?.node?.url ?? null;

  return {
    ceiling,
    mushroom,
    wall,
    latest,
    hero: heroNode && heroImage ? { node: heroNode, imageUrl: heroImage } : null,
    categories: {
      nyheder: catNyheder,
      bordet: catBordet,
      belysning: catBelysning,
      interior: catInterior,
      murano: catMurano,
      unikke: catUnikke,
    },
  };
};

export const homeHeadDa = () => {
  const loc = localizedHead({ path: "/", locale: "da" });
  return {
    meta: [
      { title: "Farveladeland — Vintage Murano-glas & loppefund" },
      {
        name: "description",
        content:
          "Et land af farver. Vintage Murano-lamper, lysekroner og sjældne loppefund — kurateret i København, sendt til hele verden.",
      },
      { property: "og:title", content: "Farveladeland — Vintage Murano-glas" },
      {
        property: "og:description",
        content:
          "Mundblæste vintage Murano-lamper i lyserød, gul, hvid og lyseblå — hånd-plukket i Italien.",
      },
      ...loc.meta,
    ],
    links: [
      ...loc.links,
      { rel: "preload", as: "image", href: heroMushroomImg, fetchpriority: "high" } as any,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(organizationJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(websiteJsonLd("da")) },
    ],
  };
};

export const homeHeadEn = () => {
  const loc = localizedHead({ path: "/", locale: "en" });
  return {
    meta: [
      { title: "Farveladeland — Vintage Murano glass & flea-market finds" },
      {
        name: "description",
        content:
          "A land of colour. Vintage Murano lamps, chandeliers and rare flea-market finds — curated in Copenhagen, shipped worldwide.",
      },
      { property: "og:title", content: "Farveladeland — Vintage Murano glass" },
      {
        property: "og:description",
        content:
          "Hand-blown vintage Murano lamps in pink, yellow, white and pale blue — hand-picked in Italy.",
      },
      ...loc.meta,
    ],
    links: [
      ...loc.links,
      { rel: "preload", as: "image", href: heroMushroomImg, fetchpriority: "high" } as any,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(organizationJsonLd()) },
      { type: "application/ld+json", children: JSON.stringify(websiteJsonLd("en")) },
    ],
  };
};

export function HomePage() {
  const data = useLoaderData({ strict: false }) as
    | Awaited<ReturnType<typeof homeLoader>>
    | undefined;
  const cats = data?.categories;
  const { t } = useT();
  useScrollDepth("home");

  return (
    <>
      <AmbientBackdrop />

      {/* SECTION 1 — HERO */}
      <HeroMushroom />

      {/* SECTION 2 — CURATED CATEGORY UNIVERSE */}
      <CategoryUniverse
        nyheder={cats?.nyheder ?? null}
        bordet={cats?.bordet ?? null}
        belysning={cats?.belysning ?? null}
        interior={cats?.interior ?? null}
        murano={cats?.murano ?? null}
        unikke={cats?.unikke ?? null}
      />

      {/* All-products CTA — quiet shortcut beneath the category grid */}
      <section className="mx-auto max-w-[1500px] px-6 pb-4 pt-2 text-center md:px-10 md:pb-8">
        <LocaleLink
          to="/alle"
          className="group inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.22em] text-foreground/80 transition-colors hover:text-terracotta"
        >
          {t("home.allProducts.cta")}
          <span aria-hidden className="transition-transform group-hover:translate-x-0.5">→</span>
        </LocaleLink>
        <p className="mt-2 text-xs text-muted-foreground">{t("home.allProducts.sub")}</p>
      </section>

      {/* SECTION 3 — SHOP BY COLOR */}
      <ColorShopStrip />

      {/* SECTION 4 — MATCHMAKER */}
      <MuranoMatchInvite />

      {/* SECTION 5 — WHY FARVELADELAND */}
      <WhyFarveladeland />

      {/* SECTION 6 — TRUSTPILOT REVIEWS */}
      <TrustpilotReviews />

      {/* SECTION 7 — BRAND PHILOSOPHY */}
      <BrandPhilosophy />
    </>
  );
}

